import {
  orderAccountsForFund,
  payFromAccount,
  purchaseCycle,
  spendingAccounts,
  type LocalDate,
} from '@tahi/core';
import { useUsable } from '../../../data/hooks';
import { useMethodOptions } from '../../../data/methodOptions';
import { copy } from '../copy';
import { monthDay } from '../format';
import { useAccounts, useBudgetSettings, useFunds, useToday } from '../hooks';
import { AccountChips } from './AccountChips';
import { FundPicker, fundHint } from './FundPicker';

export interface SpendPickersProps {
  /** Show the spending-limit chips (only when limits are on). Default true. */
  withFund?: boolean;
  fundId: string | null;
  onFund(id: string | null): void;
  accountId: string | null;
  onAccount(id: string | null): void;
  /** The account used when none is picked (e.g. a payee's own); else the limit's or "Pay from". */
  fallbackAccountId?: string | null;
  /** Hint while no limit is picked (edit sheets say something else). */
  fundHintText?: string;
  /**
   * v0.7.6: also offer the user's credit cards under "From" (Spending and the Shopping buy sheet).
   * `method` is the picked card ('card:<payee id>') or anything else; `date` is the purchase day,
   * for the "on the Dec 13 statement, due Jan 7" hint.
   */
  card?: { method: string | null; onMethod(method: string | null): void; date: LocalDate };
}

/**
 * v0.5: "Limit" chips (only while spending limits are on) and "From" account chips for anything
 * that spends money. With a limit picked, its accounts come first and its default account is
 * what "From" falls back to; otherwise Budget's "Pay from".
 */
export function SpendPickers({
  withFund = true,
  fundId,
  onFund,
  accountId,
  onAccount,
  fallbackAccountId,
  fundHintText,
  card,
}: SpendPickersProps) {
  const usable = useUsable();
  const methods = useMethodOptions();
  const cards = card ? methods.filter((m) => m.value.startsWith('card:')) : [];
  const pickedCard = card ? (cards.find((c) => c.value === card.method) ?? null) : null;
  const funds = useFunds() ?? [];
  const allAccounts = useAccounts() ?? [];
  const today = useToday();
  // v0.6: only accounts used for spending and not locked are offered (the picked one stays, so an
  // edit keeps it).
  const accounts = spendingAccounts(allAccounts, accountId, today);
  const settings = useBudgetSettings();
  if (!usable('budget') || !settings) return null;
  const limits = withFund && settings.useFunds && funds.length > 0;
  const fund = limits
    ? (funds.find((f) => f.id === (fundId ?? settings.defaultFundId)) ?? null)
    : null;
  const fallback = payFromAccount(fallbackAccountId ?? null, fund, settings, true, allAccounts);
  const fallbackName = allAccounts.find((a) => a.id === fallback)?.name;
  const cycle = pickedCard?.card && card ? purchaseCycle(pickedCard.card, card.date) : null;
  const hint = pickedCard
    ? cycle
      ? copy.cardHint(monthDay(cycle.statement), monthDay(cycle.due))
      : undefined
    : accountId === null && fallbackName && fundHintText === undefined
      ? copy.fromHint(fallbackName)
      : undefined;
  return (
    <>
      {limits ? (
        <FundPicker
          funds={funds}
          value={fundId}
          onChange={onFund}
          hint={fundHintText ?? fundHint(settings, funds)}
        />
      ) : null}
      {accounts.length > 0 || cards.length > 0 ? (
        <AccountChips
          label={copy.from}
          accounts={orderAccountsForFund(accounts, fund, settings.useFunds)}
          value={accountId}
          onChange={onAccount}
          hint={hint}
          {...(card ? { cards, card: card.method, onCard: card.onMethod } : {})}
        />
      ) : null}
    </>
  );
}
